import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'wouter';
import { useState, useRef } from 'react';
import { Loader2, Check, Upload, Mail, MessageCircle, CreditCard, Building2, Wallet, Bitcoin, Copy, CheckCircle2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/Navbar';
import { z } from 'zod';

const paymentFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  paymentMethod: z.enum(['paypal', 'bank_transfer', 'revolut_wise', 'crypto_btc', 'crypto_usdt'], {
    required_error: 'Please select a payment method',
  }),
  receiptImage: z.instanceof(File).refine(
    (file) => {
      // Check file size
      if (file.size === 0 || file.size > 50 * 1024 * 1024) return false;

      // Block executables
      const blockedExtensions = ['.exe', '.bat', '.sh', '.cmd', '.com', '.scr', '.vbs'];
      const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      return !blockedExtensions.includes(fileExtension);
    },
    'File must be less than 50MB and not an executable'
  ),
});

type PaymentFormInput = z.infer<typeof paymentFormSchema>;

const PAYMENT_OPTIONS = {
  paypal: {
    icon: Wallet,
    title: 'PayPal',
    details: [
      { label: 'Send to', value: 'your@paypal.com' },
    ],
  },
  bank_transfer: {
    icon: Building2,
    title: 'Bank Transfer',
    details: [
      { label: 'Bank', value: 'Your Bank Name' },
      { label: 'Account', value: 'XXXXXXXXXX' },
      { label: 'Reference', value: 'Use your email' },
    ],
  },
  revolut_wise: {
    icon: CreditCard,
    title: 'Revolut / Wise',
    details: [
      { label: 'Handle', value: '@yourhandle' },
    ],
  },
  crypto_btc: {
    icon: Bitcoin,
    title: 'Bitcoin (BTC)',
    details: [
      { label: 'Address', value: 'bc1qxxxxxxxxxxxxxxxxxxxxxxxxx' },
    ],
  },
  crypto_usdt: {
    icon: Bitcoin,
    title: 'USDT (TRC20)',
    details: [
      { label: 'Address', value: 'TXXXXXXXXXXXXXXXXXXXXXXXXXXx' },
    ],
  },
};

const PRO_FEATURES = [
  'Premium Daily Tips (5+)',
  'In-Depth Match Analysis',
  'Bankroll Management Tools',
  'Priority Support',
  'All Free Tier Features',
];

export default function Payment() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<PaymentFormInput>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      email: user?.email || '',
      paymentMethod: undefined,
      receiptImage: null as any,
    },
  });

  const handleCopy = async (value: string, fieldId: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload a file smaller than 50MB',
        variant: 'destructive',
      });
      return;
    }

    // Block dangerous executables for security
    const blockedExtensions = ['.exe', '.bat', '.sh', '.cmd', '.com', '.scr', '.vbs'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

    if (blockedExtensions.includes(fileExtension)) {
      toast({
        title: 'Invalid file type',
        description: 'Executable files are not allowed for security reasons',
        variant: 'destructive',
      });
      return;
    }

    // Store File object directly (no FileReader needed!)
    form.setValue('receiptImage', file);
  };

  const onSubmit = async (data: PaymentFormInput) => {
    setIsSubmitting(true);
    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('paymentMethod', data.paymentMethod);
      formData.append('receiptImage', data.receiptImage);

      const response = await fetch('/api/payment-request', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        // Handle file size error specifically
        if (response.status === 413) {
          throw new Error(error.message || 'File size exceeds the maximum limit');
        }
        throw new Error(error.error || error.message || 'Failed to submit payment request');
      }

      setSubmitted(true);
      toast({
        title: 'Payment Request Submitted!',
        description: "We'll verify your payment and activate your account shortly.",
      });
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-20 pb-10 px-4 flex items-center justify-center">
          <Card className="glass w-full max-w-lg text-center">
            <CardHeader className="px-4 sm:px-6">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-xl sm:text-2xl">Payment Request Submitted!</CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Thank you for upgrading to Pro
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 px-4 sm:px-6">
              <p className="text-sm sm:text-base text-muted-foreground">
                We'll verify your payment and activate your account within 1-2 hours (usually much faster).
                You'll receive an email confirmation once your account is upgraded.
              </p>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium">Need help?</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button variant="outline" size="sm" className="w-full sm:w-auto" asChild>
                    <a href="https://wa.me/1234567890" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 h-4 w-4" />
                      WhatsApp
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" className="w-full sm:w-auto" asChild>
                    <a href="mailto:support@example.com">
                      <Mail className="mr-2 h-4 w-4" />
                      Email Support
                    </a>
                  </Button>
                </div>
              </div>
              <Button onClick={() => setLocation('/')} className="mt-4 w-full">
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 pb-10 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold">Complete Your Pro Upgrade</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Get access to premium betting tips and analysis</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {/* Left Column - Order Summary & Payment Options */}
            <div className="space-y-4 sm:space-y-6">
              {/* Order Summary */}
              <Card className="glass">
                <CardHeader className="px-4 sm:px-6">
                  <CardTitle className="text-base sm:text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-4 sm:px-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm sm:text-base font-medium">Pro Plan</span>
                    <span className="text-xl sm:text-2xl font-bold text-primary">$29<span className="text-xs sm:text-sm text-muted-foreground">/month</span></span>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs sm:text-sm font-medium text-muted-foreground">What's included:</p>
                    <ul className="space-y-1">
                      {PRO_FEATURES.map((feature) => (
                        <li key={feature} className="text-xs sm:text-sm flex items-center gap-2">
                          <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Options */}
              <Card className="glass">
                <CardHeader className="px-4 sm:px-6">
                  <CardTitle className="text-base sm:text-lg">Payment Options</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">Choose your preferred payment method</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
                  {Object.entries(PAYMENT_OPTIONS).map(([key, option]) => {
                    const Icon = option.icon;
                    return (
                      <div key={key} className="p-3 sm:p-4 rounded-lg border bg-card/50">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                          <span className="text-sm sm:text-base font-medium">{option.title}</span>
                        </div>
                        <div className="space-y-1 sm:ml-7">
                          {option.details.map((detail, idx) => (
                            <div key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 text-xs sm:text-sm">
                              <span className="text-muted-foreground shrink-0">{detail.label}:</span>
                              <div className="flex items-center gap-2">
                                <code className="bg-muted px-2 py-0.5 rounded text-xs break-all flex-1 sm:flex-none">{detail.value}</code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 shrink-0"
                                  onClick={() => handleCopy(detail.value, `${key}-${idx}`)}
                                >
                                  {copiedField === `${key}-${idx}` ? (
                                    <Check className="h-3 w-3 text-primary" />
                                  ) : (
                                    <Copy className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Confirmation Form */}
            <div className="space-y-4 sm:space-y-6">
              <Card className="glass">
                <CardHeader className="px-4 sm:px-6">
                  <CardTitle className="text-base sm:text-lg">Confirm Your Payment</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">After sending payment, fill out this form</CardDescription>
                </CardHeader>
                <CardContent className="px-4 sm:px-6">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">Email</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="you@example.com"
                                className="text-sm"
                                {...field}
                                disabled={isSubmitting}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="paymentMethod"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">Payment Method Used</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger className="text-sm">
                                  <SelectValue placeholder="Select payment method" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="paypal" className="text-sm">PayPal</SelectItem>
                                <SelectItem value="bank_transfer" className="text-sm">Bank Transfer</SelectItem>
                                <SelectItem value="revolut_wise" className="text-sm">Revolut / Wise</SelectItem>
                                <SelectItem value="crypto_btc" className="text-sm">Bitcoin (BTC)</SelectItem>
                                <SelectItem value="crypto_usdt" className="text-sm">USDT (TRC20)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="receiptImage"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">Upload Receipt/Screenshot</FormLabel>
                            <FormControl>
                              <div className="space-y-2">
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.zip,.rar"
                                  onChange={handleFileUpload}
                                  className="hidden"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full text-sm"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={isSubmitting}
                                >
                                  <Upload className="mr-2 h-4 w-4" />
                                  {field.value ? 'Change File' : 'Upload Receipt'}
                                </Button>
                                {field.value && field.value instanceof File && (
                                  <div className="relative rounded-lg overflow-hidden border">
                                    {field.value.type.startsWith('image/') ? (
                                      <img
                                        src={URL.createObjectURL(field.value)}
                                        alt="Receipt preview"
                                        className="w-full h-32 object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-32 bg-muted flex flex-col items-center justify-center gap-2">
                                        <FileText className="h-8 w-8 text-muted-foreground" />
                                        <p className="text-xs sm:text-sm text-muted-foreground truncate px-4 max-w-full">
                                          {field.value.name}
                                        </p>
                                      </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                      <Check className="h-8 w-8 text-white" />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        className="w-full font-bold text-sm sm:text-base"
                        disabled={isSubmitting}
                      >
                        {isSubmitting && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        I've Paid - Activate My Account
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>

              {/* What Happens Next */}
              <Card className="glass">
                <CardHeader className="px-4 sm:px-6">
                  <CardTitle className="text-base sm:text-lg">What Happens Next?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 px-4 sm:px-6">
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    We'll verify your payment and activate your account within 1-2 hours (usually much faster).
                    You'll receive an email confirmation once complete.
                  </p>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Need help?</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button variant="outline" size="sm" className="w-full sm:w-auto" asChild>
                        <a href="https://wa.me/1234567890" target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp
                        </a>
                      </Button>
                      <Button variant="outline" size="sm" className="w-full sm:w-auto" asChild>
                        <a href="mailto:support@example.com">
                          <Mail className="mr-2 h-4 w-4" />
                          Email
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
